import './FilterControls.css';

import { useState } from 'react';

const FilterControls = ({
  sortBy,
  setSortBy,
  filters,
  setFilters,
  nutrientFilters,
  toggleNutrientFilter,
  clearAllFilters,
  userPlan,
  onUpgradeClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasActiveFilters =
    Object.values(filters).some(Boolean) || Object.values(nutrientFilters).some(v => v.length > 0);
  const activeFilterCount =
    Object.values(filters).filter(Boolean).length + Object.values(nutrientFilters).filter(v => v.length > 0).length;

  const isPayingUser = userPlan && userPlan !== 'free';
  const sortLabels = {
    menu_order: 'Menu Order',
    nutri_score: 'Nutri Score',
    calories_low: 'Calories: Low to High',
    calories_high: 'Calories: High to Low',
    price_low: 'Price: Low to High',
    price_high: 'Price: High to Low',
    name: 'Name: A-Z',
  };

  // Handle upgrade click
  const handleUpgradeClick = e => {
    e.stopPropagation();
    if (onUpgradeClick) {
      onUpgradeClick();
    }
  };

  return (
    <div className={`filter-controls-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="filter-controls-summary">
        <button
          type="button"
          className="filter-toggle-button"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
        >
          <span className="filter-toggle-label">⚙️ Filters</span>
          {activeFilterCount > 0 && <span className="filter-active-count">{activeFilterCount}</span>}
          <span className="filter-toggle-chevron" aria-hidden="true">
            {isExpanded ? '⌃' : '⌄'}
          </span>
        </button>

        <div className="filter-summary-meta">
          <span className="filter-summary-sort">{sortLabels[sortBy] || 'Menu Order'}</span>
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="clear-all-filters-inline compact">
              Clear
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="filter-controls-panel">
          <div className="filter-section">
            <div className="filter-section-header">
              <h4 className="filter-section-title">📊 Sort By</h4>
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="clear-all-filters-inline">
                  Clear all
                </button>
              )}
            </div>
            <div className="sort-options">
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="sort-select">
                <option value="menu_order">Menu Order</option>
                <option value="nutri_score">Nutri Score</option>
                <option value="calories_low">Calories (Low to High)</option>
                <option value="calories_high">Calories (High to Low)</option>
                <option value="price_low">Price (Low to High)</option>
                <option value="price_high">Price (High to Low)</option>
                <option value="name">Name (A-Z)</option>
              </select>
            </div>
          </div>

          <div className="filter-section">
            <h4 className="filter-section-title">🥬 Dietary Preferences</h4>
            <div className="dietary-chips">
              <label className={`filter-chip ${filters.vegetarian ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={filters.vegetarian}
                  onChange={e => setFilters(prev => ({ ...prev, vegetarian: e.target.checked }))}
                />
                <span>🥬 Vegetarian</span>
              </label>
              <label className={`filter-chip ${filters.vegan ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={filters.vegan}
                  onChange={e => setFilters(prev => ({ ...prev, vegan: e.target.checked }))}
                />
                <span>🌱 Vegan</span>
              </label>
            </div>
          </div>

          <div className={`filter-section ${!isPayingUser ? 'disabled-section' : ''}`}>
            <div className="filter-section-header">
              <h4 className="filter-section-title">⚠️ Allergens (Select the ones to avoid)</h4>
              {!isPayingUser && <div className="premium-badge" title="Upgrade to unlock allergen filters"></div>}
            </div>
            <div className={`allergen-chips ${!isPayingUser ? 'blurred' : ''}`}>
              {[
                // Most Common Allergens
                { key: 'milk', label: 'Milk', icon: '🥛' },
                { key: 'cereals_containing_gluten', label: 'Gluten', icon: '🌾' },
                { key: 'eggs', label: 'Eggs', icon: '🥚' },
                { key: 'peanuts', label: 'Peanuts', icon: '🥜' },
                { key: 'nuts', label: 'Tree Nuts', icon: '🌰' },
                { key: 'soybeans', label: 'Soybeans', icon: '🌱' },
                { key: 'fish', label: 'Fish', icon: '🐟' },
                { key: 'crustaceans', label: 'Crustaceans', icon: '🦐' },
                { key: 'molluscs', label: 'Molluscs', icon: '🦪' },
                // Moderately Common or Region-Specific
                { key: 'sesame_seeds', label: 'Sesame Seeds', icon: '⚫' },
                { key: 'celery', label: 'Celery', icon: '🌿' },
                { key: 'mustard', label: 'Mustard', icon: '🌭' },
                // Less Common Allergens
                { key: 'lupin', label: 'Lupin', icon: '🌾' },
                { key: 'sulphur_dioxide_and_sulphites', label: 'Sulphites', icon: '🍷' },
              ].map(allergen => (
                <label
                  key={allergen.key}
                  className={`filter-chip ${nutrientFilters[allergen.key].includes('high') ? 'active' : ''} ${
                    !isPayingUser ? 'disabled' : ''
                  }`}
                  title={
                    !isPayingUser
                      ? `🔒 Upgrade to filter by ${allergen.label}`
                      : `Filter out items with high ${allergen.label} risk`
                  }
                >
                  <input
                    type="checkbox"
                    checked={nutrientFilters[allergen.key].includes('high')}
                    onChange={() => isPayingUser && toggleNutrientFilter(allergen.key, 'high')}
                    disabled={!isPayingUser}
                  />
                  <span>
                    {allergen.icon} {allergen.label}
                  </span>
                </label>
              ))}
            </div>
            {!isPayingUser && (
              <div className="upgrade-overlay" onClick={handleUpgradeClick}>
                <div className="upgrade-message">
                  <span className="upgrade-icon">🔒</span>
                  <span>Upgrade to unlock allergen filters</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;
